import { Api } from 'Utils/api';
import type { ContactList, ContactListMember, GhlContact } from './types';
import type { PaginatedResponse } from '../shared/types';

const getHeaders = (ssoToken: string) => ({
  'X-GHL-SSO': ssoToken,
});

export interface SearchContactsParams {
  search?: string;
  type?: string;
  tags?: string[];
  dnd?: boolean;
  sort?: string;
  page?: number;
  limit?: number;
}

export interface SearchContactsResult {
  contacts: GhlContact[];
  meta: { current_page: number; last_page: number; total: number };
}

const normalizeContact = (c: any): GhlContact => ({
  id: c.id,
  display_name: c.display_name || c.displayName || null,
  first_name: c.first_name || c.firstName || null,
  last_name: c.last_name || c.lastName || null,
  email: c.email || null,
  phone: c.phone || null,
  type: c.type || c.contactType || null,
  company_name: c.company_name || c.companyName || c.business?.name || null,
  city: c.city || null,
  state: c.state || null,
  source: c.source || null,
  tags: Array.isArray(c.tags) ? c.tags : null,
  dnd: typeof c.dnd === 'boolean' ? c.dnd : null,
});

export const searchContacts = async (
  ssoToken: string,
  params: SearchContactsParams = {},
): Promise<SearchContactsResult> => {
  const queryParams: Record<string, string | number | string[]> = {
    limit: params.limit || 50,
    page: params.page || 1,
  };
  if (params.search?.trim()) queryParams['filter[search]'] = params.search.trim();
  if (params.type) queryParams['filter[type]'] = params.type;
  if (params.tags?.length) queryParams['filter[tag][]'] = params.tags;
  if (params.dnd !== undefined) queryParams['filter[dnd]'] = params.dnd ? '1' : '0';
  if (params.sort) queryParams.sort = params.sort;

  const response = await Api.get('/ghl/embed/contacts', {
    params: queryParams,
    headers: getHeaders(ssoToken),
  });

  // Handle both GHL format { contacts: [...] } and Laravel format { data: [...] }
  const raw = Array.isArray(response?.data?.contacts)
    ? response.data.contacts
    : Array.isArray(response?.data?.data)
      ? response.data.data
      : Array.isArray(response?.data)
        ? response.data
        : [];

  const meta = response?.data?.meta || {
    current_page: params.page || 1,
    last_page: 1,
    total: raw.length,
  };

  return {
    contacts: raw.map(normalizeContact),
    meta: { current_page: meta.current_page, last_page: meta.last_page, total: meta.total },
  };
};

export const fetchAvailableTags = async (ssoToken: string): Promise<string[]> => {
  const response = await Api.get('/ghl/embed/contacts/tags', {
    headers: getHeaders(ssoToken),
  });
  return response?.data?.data ?? [];
};

export const listContactLists = async (
  companyId: string,
  ssoToken: string,
  page = 1,
): Promise<PaginatedResponse<ContactList>> => {
  const response = await Api.get(`/companies/${companyId}/ghl/contact-lists`, {
    params: { page },
    headers: getHeaders(ssoToken),
  });
  return response.data;
};

export const createContactList = async (
  companyId: string,
  data: { name: string; description?: string; contact_ids?: string[] },
  ssoToken: string,
): Promise<{ data: ContactList }> => {
  const response = await Api.post(`/companies/${companyId}/ghl/contact-lists`, data, {
    headers: getHeaders(ssoToken),
  });
  return response.data;
};

export const getContactList = async (
  companyId: string,
  listId: number,
  ssoToken: string,
): Promise<{ data: ContactList }> => {
  const response = await Api.get(`/companies/${companyId}/ghl/contact-lists/${listId}`, {
    headers: getHeaders(ssoToken),
  });
  return response.data;
};

export const updateContactList = async (
  companyId: string,
  listId: number,
  data: { name?: string; description?: string },
  ssoToken: string,
): Promise<{ data: ContactList }> => {
  const response = await Api.put(`/companies/${companyId}/ghl/contact-lists/${listId}`, data, {
    headers: getHeaders(ssoToken),
  });
  return response.data;
};

export const deleteContactList = async (
  companyId: string,
  listId: number,
  ssoToken: string,
): Promise<void> => {
  await Api.delete(`/companies/${companyId}/ghl/contact-lists/${listId}`, {
    headers: getHeaders(ssoToken),
  });
};

export const listMembers = async (
  companyId: string,
  listId: number,
  ssoToken: string,
  page = 1,
): Promise<PaginatedResponse<ContactListMember>> => {
  const response = await Api.get(`/companies/${companyId}/ghl/contact-lists/${listId}/members`, {
    params: { page },
    headers: getHeaders(ssoToken),
  });
  const raw = response.data;
  // Backend returns flat Laravel pagination; normalize to PaginatedResponse shape
  return {
    data: raw.data,
    meta: raw.meta ?? {
      current_page: raw.current_page,
      last_page: raw.last_page,
      per_page: raw.per_page,
      total: raw.total,
    },
  };
};

export const addMembers = async (
  companyId: string,
  listId: number,
  contactIds: string[],
  ssoToken: string,
): Promise<void> => {
  await Api.post(`/companies/${companyId}/ghl/contact-lists/${listId}/members`, {
    contact_ids: contactIds,
  }, {
    headers: getHeaders(ssoToken),
  });
};

export const removeMembers = async (
  companyId: string,
  listId: number,
  contactIds: string[],
  ssoToken: string,
): Promise<void> => {
  await Api.delete(`/companies/${companyId}/ghl/contact-lists/${listId}/members`, {
    data: { contact_ids: contactIds },
    headers: getHeaders(ssoToken),
  });
};
